import { ActionType, createAction, createAsyncAction } from "typesafe-actions";

import MarkerShape from "@mapmycustomers/shared/enum/MarkerShape";
import { Company, Person } from "@mapmycustomers/shared/types/entity";
import PersistentBaseMapLayerData from "@mapmycustomers/shared/types/persistent/PersistentBaseMapLayerData";
import User from "@mapmycustomers/shared/types/User";
import MapViewState from "@mapmycustomers/shared/types/viewModel/MapViewState";

import UserLocationDatePeriod from "./enums/UserLocationDatePeriod";
import { setPlayback } from "./playback/actions";
import HoveredUserLocationDayGroup from "./types/HoveredUserLocationDayGroup";
import IsoDateRange from "./types/IsoDateRange";
import UserLocation from "./types/UserLocation";
import UserLocationTimelineEvent from "./types/UserLocationTimelineEvent";
import WidgetsData from "./types/WidgetsData";

export const initUserLocations = createAsyncAction(
  "map/userLocations/initUserLocations/request",
  "map/userLocations/initUserLocations/success",
  "map/userLocations/initUserLocations/failure"
)<
  void,
  {
    disabledUserIds: User["id"][];
  },
  unknown
>();

export const disposeUserLocations = createAction("map/userLocations/disposeUserLocations")<void>();

export const fetchCheckInPins = createAsyncAction(
  "map/userLocations/fetchCheckInPins/request",
  "map/userLocations/fetchCheckInPins/success",
  "map/userLocations/fetchCheckInPins/failure"
)<
  void,
  {
    accountPins: Record<Company["id"], Company & { shape?: MarkerShape }>;
    contactPins: Record<Person["id"], Person & { shape?: MarkerShape }>;
  },
  unknown
>();

export const fetchEvents = createAsyncAction(
  "map/userLocations/fetchEvents/request",
  "map/userLocations/fetchEvents/success",
  "map/userLocations/fetchEvents/failure"
)<{ dateRange: IsoDateRange }, { events: UserLocationTimelineEvent[] }, unknown>();

export const setUserLocationsSettings = createAction("map/userLocations/setUserLocationsSettings")<
  PersistentBaseMapLayerData | undefined
>();

export const setHighlightedLocationId = createAction("map/userLocations/setHighlightedLocationId")<
  undefined | UserLocation["id"]
>();

export const setHoveredTrailPoint = createAction("map/userLocations/setHoveredTrailPoint")<
  google.maps.LatLngLiteral | undefined
>();

export const setHoveredUserLocationDayGroup = createAction(
  "map/userLocations/setHoveredUserLocationDayGroup"
)<HoveredUserLocationDayGroup | undefined>();

export const selectLocation = createAction("map/userLocations/selectLocation")<
  undefined | UserLocation["id"]
>();

export const setDisabledUserIds = createAction("map/userLocations/setDisabledUserIds")<
  User["id"][]
>();

export const setSelectedUserIds = createAction("map/userLocations/setSelectedUserIds")<
  User["id"][]
>();

export const setManualHeatmapMode = createAction(
  "map/userLocations/setManualHeatmapMode"
)<boolean>();

export const setShowOtherPins = createAction("map/userLocations/setShowOtherPins")<boolean>();

export const setSelectedDatePeriod = createAction("map/userLocations/setSelectedDatePeriod")<{
  // The reducer resolves the org's calendar day from this, because selectedDate becomes an
  // org-timezone query range downstream.
  orgTimeZone: string | undefined;
  period: UserLocationDatePeriod;
  // Callers that already know the exact day — the efficiency-chart drill-down — pass it here, so the
  // reducer does not resolve a date the caller would replace with a second action.
  selectedDate?: Date;
}>();

export const setSelectedDate = createAction("map/userLocations/setSelectedDate")<
  Date | undefined
>();

export const fetchWidgetsData = createAsyncAction(
  "map/userLocations/fetchWidgetsData/request",
  "map/userLocations/fetchWidgetsData/success",
  "map/userLocations/fetchWidgetsData/failure"
)<
  {
    dateRange: IsoDateRange;
    userIds: User["id"][];
  },
  {
    data: WidgetsData;
  },
  unknown
>();

export const invalidateWidgetsData = createAction(
  "map/userLocations/invalidateWidgetsData"
)<void>();

export const colorOrShapeKeyChanged = createAction("@userLocations/colorOrShapeKeyChanged")<{
  colorKey?: MapViewState["colorKey"];
  shapeKey?: MapViewState["shapeKey"];
}>();

export * from "./playback/actions";

export type UserLocationActions = ActionType<
  | typeof colorOrShapeKeyChanged
  | typeof disposeUserLocations
  | typeof fetchCheckInPins
  | typeof fetchEvents
  | typeof fetchWidgetsData
  | typeof initUserLocations
  | typeof invalidateWidgetsData
  | typeof selectLocation
  | typeof setDisabledUserIds
  | typeof setHighlightedLocationId
  | typeof setHoveredTrailPoint
  | typeof setHoveredUserLocationDayGroup
  | typeof setManualHeatmapMode
  | typeof setPlayback
  | typeof setSelectedDate
  | typeof setSelectedDatePeriod
  | typeof setSelectedUserIds
  | typeof setShowOtherPins
  | typeof setUserLocationsSettings
>;
